import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { EntityRecord } from 'lib/api/record';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { CreditLine, Warranty } from 'lib/types/models';
import React, { useMemo } from 'react';

type RecordType = CreditLine | Warranty;

export type Props = {
  record: EntityRecord<RecordType>;
};

const EmptyHistory: React.FC = () => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" py={3}>
      <Stack spacing={1} alignItems="center">
        <HistoryIcon sx={{ color: 'text.disabled', fontSize: 40 }} />
        <Typography variant="body2" color="text.secondary">
          Sin historial disponible.
        </Typography>
      </Stack>
    </Box>
  );
};

const StatusRecord: React.FC<Props> = ({ record }) => {
  const items = useMemo(() => {
    if (!record) return [] as EntityRecord<RecordType>[];
    return Array.isArray(record as unknown as EntityRecord<RecordType>[])
      ? (record as unknown as EntityRecord<RecordType>[])
      : [record];
  }, [record]);

  const sortedItems = useMemo(() => {
    return [...items].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    );
  }, [items]);

  const getOperationIconAndColor = (
    operation: EntityRecord<RecordType>['operation'] | undefined,
  ) => {
    switch (operation) {
      case 'CREATE':
        return { icon: <AddCircleOutlineIcon />, color: BASE_COLORS.green };
      case 'UPDATE':
        return { icon: <EditIcon />, color: BASE_COLORS.blue };
      case 'DELETE':
        return { icon: <DeleteIcon />, color: BASE_COLORS.red };
      default:
        return { icon: <HistoryIcon />, color: BASE_COLORS.gray };
    }
  };

  const getOperationChipProps = (
    operation: EntityRecord<RecordType>['operation'] | undefined,
  ) => {
    switch (operation) {
      case 'CREATE':
        return { label: 'Creación' };
      case 'UPDATE':
        return { label: 'Actualización' };
      case 'DELETE':
        return { label: 'Eliminación' };
      default:
        return { label: 'Historial' };
    }
  };

  const formatDateTime = (ts: number | string) => {
    const millis = typeof ts === 'string' ? Number(ts) : ts;
    return new Date(millis).toLocaleString();
  };

  if (!sortedItems.length) return <EmptyHistory />;

  return (
    <Timeline position="right">
      {sortedItems.map((entry, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === sortedItems.length - 1;
        const status = entry.after?.status as unknown as string | undefined;
        const translatedStatus = status ?? 'Estado desconocido';
        const { icon, color } = getOperationIconAndColor(entry.operation);
        return (
          <TimelineItem
            key={`${entry.entity}-${entry.entityId}-${entry.timestamp}-${idx}`}
          >
            <TimelineOppositeContent
              sx={{ m: 'auto 0', textAlign: 'right' }}
              variant="body2"
              color="text.secondary"
            >
              {formatDateTime(entry.timestamp)}
            </TimelineOppositeContent>
            <TimelineSeparator>
              {!isFirst && <TimelineConnector />}
              <TimelineDot sx={{ bgcolor: color }}>{icon}</TimelineDot>
              {!isLast && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent sx={{ py: '8px', px: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="subtitle1"
                  component="div"
                  fontWeight={600}
                >
                  {translatedStatus}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{
                    color,
                    borderColor: color,
                    fontWeight: 500,
                  }}
                  {...getOperationChipProps(entry.operation)}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {entry.after?.comitee?.name ?? 'No hay comité asignado'}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
};

export default StatusRecord;
